/**
 * Data migration utility for backward compatibility
 * Handles migration from old data formats to new ones
 */

import type { Node } from '../types';
import { DEFAULT_DIMENSIONS } from '../types/elements';

const STORAGE_KEY = 'excaliplan-state';
const BACKUP_KEY = 'excaliplan-state_backup_v1';

export interface MigrationResult {
  success: boolean;
  version: number;
  message: string;
  backupCreated: boolean;
}

/**
 * Check if migration is needed
 */
export function needsMigration(): boolean {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return false;

  try {
    const data = JSON.parse(stored);
    // Version 2 is current version with element system
    return !data._version || data._version < 2;
  } catch {
    return false;
  }
}

/**
 * Migrate a v1 Node to include width/height properties
 * This allows gradual transition to the full element system
 */
function migrateNodeAddDimensions(node: Node): Node & { width: number; height: number } {
  const dimensions = DEFAULT_DIMENSIONS[node.type] || DEFAULT_DIMENSIONS.task;

  return {
    ...node,
    width: dimensions.width,
    height: dimensions.height,
  };
}

/**
 * Migrate localStorage data from v1 to v2
 * V1: nodes without width/height, connections without handles
 * V2: nodes with dimensions, connections with handle positions (future)
 */
export function migrateLocalStorageData(): MigrationResult {
  const stored = localStorage.getItem(STORAGE_KEY);

  if (!stored) {
    return {
      success: true,
      version: 2,
      message: 'No data to migrate',
      backupCreated: false,
    };
  }

  try {
    const data = JSON.parse(stored);

    // Already migrated?
    if (data._version === 2) {
      return {
        success: true,
        version: 2,
        message: 'Data already at current version',
        backupCreated: false,
      };
    }

    // Create backup before migration
    localStorage.setItem(BACKUP_KEY, stored);
    console.log('[Migration] Backup created at:', BACKUP_KEY);

    // Migrate nodes: add width/height properties
    const migratedNodes: Record<string, any> = {};
    Object.entries(data.nodes || {}).forEach(([id, node]) => {
      migratedNodes[id] = migrateNodeAddDimensions(node as Node);
    });

    // Migrate connections: add default handle positions (will be updated in Phase 2)
    const migratedConnections = (data.connections || []).map((conn: any) => ({
      ...conn,
      fromHandle: conn.fromHandle || 'right',
      toHandle: conn.toHandle || 'left',
    }));

    // Save migrated data
    const migratedData = {
      _version: 2,
      nodes: migratedNodes,
      connections: migratedConnections,
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(migratedData));

    console.log('[Migration] Successfully migrated to v2');
    console.log('[Migration] Migrated nodes:', Object.keys(migratedNodes).length);
    console.log('[Migration] Migrated connections:', migratedConnections.length);

    return {
      success: true,
      version: 2,
      message: `Successfully migrated ${Object.keys(migratedNodes).length} nodes and ${migratedConnections.length} connections`,
      backupCreated: true,
    };
  } catch (error) {
    console.error('[Migration] Failed:', error);
    return {
      success: false,
      version: 1,
      message: `Migration failed: ${error}`,
      backupCreated: false,
    };
  }
}

/**
 * Restore from backup
 */
export function restoreFromBackup(): boolean {
  const backup = localStorage.getItem(BACKUP_KEY);
  if (!backup) {
    console.error('[Migration] No backup found');
    return false;
  }

  try {
    localStorage.setItem(STORAGE_KEY, backup);
    console.log('[Migration] Restored from backup');
    return true;
  } catch (error) {
    console.error('[Migration] Failed to restore from backup:', error);
    return false;
  }
}

/**
 * Get migration info for debugging
 */
export function getMigrationInfo() {
  const stored = localStorage.getItem(STORAGE_KEY);
  const backup = localStorage.getItem(BACKUP_KEY);

  return {
    hasData: !!stored,
    hasBackup: !!backup,
    currentVersion: stored ? JSON.parse(stored)._version || 1 : null,
    needsMigration: needsMigration(),
  };
}
